<script lang="ts">
  import HealthBar from './components/health-bar.svelte';
  import { createPlayerInstance, dealDamage, PlayerClass, type Player } from './models/player.type';
  import { addModifier, getModifiers } from './models/player.type';
  import { TraitType } from './models/trait-registry';
  import { StatusEffect } from './models/status-effects.type';
  import { type Die, DieMaterial, DieType, createDie } from './models/die.type';
  import DieElement from './dice-tray/die.svelte';
  import Chrome from './components/chrome.svelte';
  import Portrait from './components/portrait.svelte';
  import Particles from './components/particle.svelte';

  let playerUI = $state(createPlayerInstance(PlayerClass.Fighter, TraitType.Warrior));
  dealDamage({
    target: playerUI,
    value: 4,
    source: playerUI,
    dice: [],
  });
  addModifier(playerUI, StatusEffect.Poison, 2);
  addModifier(playerUI, StatusEffect.Freezing, 2);
  addModifier(playerUI, StatusEffect.Block, 2);
  addModifier(playerUI, StatusEffect.Barrier, 2);
  addModifier(playerUI, StatusEffect.Regen, 2);

  const dieTypes = [
    DieType.Shortsword,
    DieType.Spear,
    DieType.Shield,
    DieType.Bracer,
    DieType.Dagger,
    DieType.Cloak,
    DieType.Ice,
    DieType.Earth,
    DieType.Flask,
  ];
  let dice: Die[][] = [[], [], []];
  dieTypes.forEach((type, index) => {
    dice[0].push(
      createDie({
        type,
        level: Math.min(2, index - 2),
        material: DieMaterial.Normal,
      }),
    );
  });
  dieTypes.forEach((type, index) => {
    dice[1].push(
      createDie({
        type,
        level: Math.min(2, index - 2),
        material: DieMaterial.Stone,
      }),
    );
  });
  dieTypes.forEach((type, index) => {
    dice[2].push(
      createDie({
        type,
        level: Math.min(2, index - 2),
        material: DieMaterial.Invoked,
      }),
    );
  });

  const colours = [
    'green',
    'cyan',
    'light-blue',
    'dark-blue',
    'purple',
    'pink',
    'red',
    'orange',
    'yellow',
    'gray',
    'brown',
    'black',
  ];

  const chromes: Array<'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'obsidian'> = [
    'silver',
    'gold',
    'bronze',
    'obsidian',
    'diamond',
    'platinum',
  ];

  const backgrounds = [
    'none',
    'cave',
    'coast',
    'desert',
    'forest',
    'lake',
    'mountain',
    'plains',
    'town-alley',
    'town-arena',
    'town-barracks',
    'swamp',
    'corestone-chamber',
    'river',
    'elemental-nexus',
  ];
  let background = $state(backgrounds[1]);

  const portraitsMonster = [
    'wasp',
    'wolf',
    'vampire',
    'ghoul',
    'troll',
    'orc-warrior',
    'orc-beserker',
    'orc-lancer',
    'orc-brawler',
    'goblin-scrapper',
    'goblin-archer',
    'goblin-assassin',
    'minotaur',
    'elemental-fire',
    'elemental-ice',
    'elemental-lightning',
    'elemental-earth',
    'training-dummy',
    'snake',
    'sand-wyrm',
    'bandit-ranger',
    'bandit-collector',
    'bandit-thug',
    'bandit-rogue',
    'bandit-trapper',
    'bandit-brawler',
    'harpy',
    'siren',
    'crow',
    'crab',
    'scorpion',
    'spider',
    'phoenix',
    'warden',
    'kraken',
    'cerberus',
    'corestone-guardian',
    'wasp-swarm',
    'rat-plague',
    'water-devils',
    'giant',
    'manticore',
    'beholder',
    'black-knight',
  ];
  let portraitMonster = $state(portraitsMonster[0]);

  const portraitsPlayer: Array<string> = [
    PlayerClass.Fighter,
    TraitType.Knight,
    TraitType.Barbarian,
    TraitType.Warrior,
    TraitType.Gladiator,
    PlayerClass.Rogue,
    TraitType.ShadowWalker,
    TraitType.Bladestorm,
    TraitType.Fencer,
    TraitType.SpellSlinger,
    PlayerClass.Elementalist,
    TraitType.Anarchist,
    TraitType.ManaBorn,
    TraitType.Wielder,
    TraitType.Dryad,
    PlayerClass.Ranger,
    TraitType.Archer,
    TraitType.Tamer,
    TraitType.Piper,
    TraitType.Arbelist,
  ];
  let portraitPlayer = $state(portraitsPlayer[0]);
  function playerChanged(event: Event) {
    const select = event.target as HTMLSelectElement;
    let index = portraitsPlayer.indexOf(select.value);
    const root: HTMLElement | null = document.querySelector(':root');
    if (index < 5) {
      root?.style.setProperty('--player-colour', '#B33A2E');
    } else if (index < 10) {
      root?.style.setProperty('--player-colour', '#3a8843');
    } else if (index < 15) {
      root?.style.setProperty('--player-colour', '#1F3C58');
    } else {
      root?.style.setProperty('--player-colour', '#E3C76D');
    }
  }
  const root: HTMLElement | null = document.querySelector(':root');
  root?.style.setProperty('--player-colour', '#B33A2E');

  const portraitsLocation = [
    'watchtower',
    'talltree',
    'coins2',
    'map1',
    'campfire1',
    'altarShield',
    'altarStealth',
    'corestone',
    'potion1',
    'injury1',
    'blessing1',
    'curse1',
    'salesman-potion',
    'salesman-bomb',
    'wagon',
    'raven-watch',
    'signpost',
    'stone-arch',
    'cliff',
    'bandit-mob',
    'civilians',
    'omphalos-gate',
    'cartographer',
  ];
  let portraitLocation = $state(portraitsLocation[0]);

  function getStacks(effect: StatusEffect) {
    const modifierList = getModifiers(playerUI).filter((mod) => mod.type == effect);
    if (modifierList.length > 0) {
      return modifierList[0].stacks;
    }
    return 0;
  }

  function setStacks(effect: StatusEffect, stacks: number) {
    const modifierList = getModifiers(playerUI).filter((mod) => mod.type == effect);
    if (modifierList.length > 0) {
      modifierList[0].stacks = stacks;
    }
    // Trigger reactivity
    playerUI = { ...playerUI };
  }

  function spawnParticle(type: 'damage' | 'healing' | 'icon') {
    if (type == 'damage') {
      let event = new CustomEvent('createParticle', {
        detail: {
          type: 'text',
          animation: 'fall',
          value: 1,
          spawnTarget: '.portrait.warrior',
          colour: 'red',
        },
      });
      window.dispatchEvent(event);
    } else if (type == 'healing') {
      let event = new CustomEvent('createParticle', {
        detail: {
          type: 'text',
          animation: 'rise',
          value: 1,
          spawnTarget: '.portrait.warrior',
          colour: 'green',
        },
      });
      window.dispatchEvent(event);
    } else if (type == 'icon') {
      let event = new CustomEvent('createParticle', {
        detail: {
          type: 'icon',
          animation: 'grow',
          value: 'shield',
          spawnTarget: '.portrait.warrior',
        },
      });
      window.dispatchEvent(event);
    }
  }
</script>

<div class="container {background}">
  <div class="dim">
    <h1>Backgrounds</h1>

    <select bind:value={background}>
      {#each backgrounds as bg}
        <option value={bg}>{bg.charAt(0).toUpperCase() + bg.slice(1).replaceAll('-', ' ')}</option>
      {/each}
    </select>
  </div>

  <h2>Colour Palette</h2>
  <div class="row">
    {#each colours as colour}
      <div class="colour-swatch {colour}">
        <p>{colour}</p>
      </div>
    {/each}
  </div>

  <h2>Portrait Component</h2>
  <div class="row">
    <div>
      <p>Player</p>
      <Portrait data={{ type: portraitPlayer, id: 'player' }} />
      <select bind:value={portraitPlayer} onchange={playerChanged}>
        {#each portraitsPlayer as p}
          <option value={p}>{p.charAt(0).toUpperCase() + p.slice(1).replaceAll('-', ' ')}</option>
        {/each}
      </select>
    </div>

    <div>
      <p>Monster</p>
      <Portrait data={{ type: portraitMonster }} />
      <select bind:value={portraitMonster}>
        {#each portraitsMonster as p}
          <option value={p}>{p.charAt(0).toUpperCase() + p.slice(1).replaceAll('-', ' ')}</option>
        {/each}
      </select>
    </div>

    <div>
      <p>Location</p>
      <Portrait data={{ type: portraitLocation }} />
      <select bind:value={portraitLocation}>
        {#each portraitsLocation as p}
          <option value={p}>{p.charAt(0).toUpperCase() + p.slice(1).replaceAll('-', ' ')}</option>
        {/each}
      </select>
    </div>
  </div>

  <h2>Status Effects</h2>
  <div class="row">
    <div>
      <Portrait data={playerUI} />
    </div>
    <div>
      <button
        onclick={() => {
          spawnParticle('damage');
        }}>Spawn Damage Particle</button
      >
      <button
        onclick={() => {
          spawnParticle('healing');
        }}>Spawn Healing Particle</button
      >
      <button
        onclick={() => {
          spawnParticle('icon');
        }}>Spawn Icon Particle</button
      >
    </div>
  </div>

  <div class="dim">
    <h1>Player Health</h1>

    <p>Health: {playerUI.healthCurrent} / {playerUI.healthMax}</p>
    <input type="range" min="0" max={playerUI.healthMax} bind:value={playerUI.healthCurrent} />

    <p>Poison: {getStacks(StatusEffect.Poison)}</p>
    <input
      type="range"
      min="0"
      max="10"
      value="2"
      oninput={(e) =>
        setStacks(StatusEffect.Poison, Number((e.target as HTMLInputElement)?.value || 0))}
    />

    <p>Freezing: {getStacks(StatusEffect.Freezing)}</p>
    <input
      type="range"
      min="0"
      max="10"
      value="2"
      oninput={(e) =>
        setStacks(StatusEffect.Freezing, Number((e.target as HTMLInputElement)?.value || 0))}
    />

    <p>Regen: {getStacks(StatusEffect.Regen)}</p>
    <input
      type="range"
      min="0"
      max="10"
      value="2"
      oninput={(e) =>
        setStacks(StatusEffect.Regen, Number((e.target as HTMLInputElement)?.value || 0))}
    />

    <p>Block: {getStacks(StatusEffect.Block)}</p>
    <input
      type="range"
      min="0"
      max="10"
      value="2"
      oninput={(e) =>
        setStacks(StatusEffect.Block, Number((e.target as HTMLInputElement)?.value || 0))}
    />

    <p>Barrier: {getStacks(StatusEffect.Barrier)}</p>
    <input
      type="range"
      min="0"
      max="10"
      value="2"
      oninput={(e) =>
        setStacks(StatusEffect.Barrier, Number((e.target as HTMLInputElement)?.value || 0))}
    />
  </div>

  <!-- <h2>Health Bar - light</h2>
  <div class='health-bar'>
    <div class='bar'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar2'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar3'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar4'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar5'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar6'>
      <HealthBar data={playerUI} />
    </div>
  </div>

  <h2>Health Bar - mid</h2>
  <div class='health-bar mid'>
    <div class='bar'>
      <HealthBar data={playerUI} />
    </div>

    <div class='bar bar2'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar3'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar4'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar5'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar6'>
      <HealthBar data={playerUI} />
    </div>
  </div> -->

  <h2>Health Bar - dark</h2>
  <div class="health-bar dark">
    <div class="bar">
      <HealthBar data={playerUI} />
    </div>

    <!-- <div class='bar bar2'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar3'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar4'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar5'>
      <HealthBar data={playerUI} />
    </div>
    <div class='bar bar6'>
      <HealthBar data={playerUI} />
    </div> -->
  </div>

  <div>
    <h2>Die Element</h2>
    <h2>Die Element - Standard Material</h2>
    <div class="row">
      {#each dice[0] as die, index}
        <div class="die-container" style="background-color: var(--{die.colour});">
          <DieElement {die} die3D={false} hover={true} />
        </div>
      {/each}
    </div>

    <h2>Die Element - Stone Material</h2>
    <div class="row">
      {#each dice[1] as die, index}
        <DieElement {die} die3D={false} hover={true} />
      {/each}
    </div>

    <h2>Die Element - Invoked Material</h2>
    <div class="row">
      {#each dice[2] as die, index}
        <DieElement {die} die3D={false} hover={true} />
      {/each}
    </div>
  </div>

  <div>
    {#each chromes as chrome}
      <h2>
        Chrome Component - {chrome.charAt(0).toUpperCase() + chrome.slice(1)}
      </h2>
      <div class="row">
        <Chrome chroming={chrome} edge="square" thickness="thick" gloss={true}>
          <div class="padding">Text</div>
        </Chrome>

        <Chrome chroming={chrome} edge="round" thickness="thick" gloss={true}>
          <div class="padding">Text</div>
        </Chrome>

        <Chrome chroming={chrome} edge="circle" thickness="thick" gloss={true}>
          <div class="padding">Text</div>
        </Chrome>

        <Chrome chroming={chrome} edge="square" thickness="thin" gloss={true}>
          <div class="padding">Text</div>
        </Chrome>

        <Chrome chroming={chrome} edge="round" thickness="thin" gloss={true}>
          <div class="padding">Text</div>
        </Chrome>

        <Chrome chroming={chrome} edge="circle" thickness="thin" gloss={true}>
          <div class="padding">Text</div>
        </Chrome>
      </div>
    {/each}
  </div>
  <Particles />
</div>

<style lang="less">
  @import '../defines.less';

  .container {
    overflow: auto;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    position: absolute;
    background-size: cover;

    .dim {
      background-color: rgba(0, 0, 0, 0.6);
      padding: 20px;
    }

    h1,
    h2 {
      margin-bottom: 10px;
    }

    h1 {
      font-size: 32px;
    }

    h2 {
      font-size: 24px;
    }

    select {
      margin-bottom: 20px;
      font-size: 16px;
      padding: 10px;
    }

    input[type='range'] {
      width: 100%;
      max-width: 300px;
      margin-bottom: 20px;
      appearance: none;
      border-radius: 16px;
      box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.08);
      outline: none;
      transition: box-shadow 0.2s;

      &:focus {
        box-shadow: 0 0 0 3px #bfa76a44;
      }

      &::-webkit-slider-thumb {
        appearance: none;
        width: 32px;
        height: 32px;
        margin-top: -12px;
        border-radius: 50%;
        background: linear-gradient(135deg, #bfa76a 60%, #fff 100%);
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
        border: 2px solid #bfa76a;
        cursor: pointer;
        transition: background 0.2s;
      }
      &::-moz-range-thumb {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: linear-gradient(135deg, #bfa76a 60%, #fff 100%);
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
        border: 2px solid #bfa76a;
        cursor: pointer;
        transition: background 0.2s;
      }
      &::-ms-thumb {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: linear-gradient(135deg, #bfa76a 60%, #fff 100%);
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
        border: 2px solid #bfa76a;
        cursor: pointer;
        transition: background 0.2s;
      }
      &::-webkit-slider-runnable-track {
        height: 8px;
        border-radius: 8px;
        background: #e0e0e0;
      }
      &::-ms-fill-lower {
        background: #e0e0e0;
        border-radius: 8px;
      }
      &::-ms-fill-upper {
        background: #c2b8a3;
        border-radius: 8px;
      }
      &::-moz-range-track {
        height: 8px;
        border-radius: 8px;
        background: #e0e0e0;
      }
      &:active::-webkit-slider-thumb {
        background: linear-gradient(135deg, #bfa76a 80%, #fff 100%);
      }
      &:active::-moz-range-thumb {
        background: linear-gradient(135deg, #bfa76a 80%, #fff 100%);
      }
      &:active::-ms-thumb {
        background: linear-gradient(135deg, #bfa76a 80%, #fff 100%);
      }
    }

    .row {
      display: flex;
      flex-direction: row;
      grid-gap: 20px;
      margin-bottom: 40px;
      position: relative;

      .padding {
        padding: 10px;
      }
    }

    .die-container {
      position: relative;
      padding: 10px;
    }

    .colour-swatch {
      width: 100px;
      height: 100px;
      border: 1px solid black;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;

      &.green {
        background-color: var(--green);
      }
      &.cyan {
        background-color: var(--cyan);
        color: black;
        p {
          text-shadow: 1px 1px 2px gray;
        }
      }
      &.light-blue {
        background-color: var(--light-blue);
        color: black;
        p {
          text-shadow: 1px 1px 2px gray;
        }
      }
      &.dark-blue {
        background-color: var(--dark-blue);
      }
      &.purple {
        background-color: var(--purple);
      }
      &.pink {
        background-color: var(--pink);
      }
      &.red {
        background-color: var(--red);
      }
      &.orange {
        background-color: var(--orange);
      }
      &.yellow {
        background-color: var(--yellow);
        color: black;
        p {
          text-shadow: 1px 1px 2px gray;
        }
      }
      &.gray {
        background-color: var(--gray);
      }
      &.brown {
        background-color: var(--brown);
      }
      &.black {
        background-color: var(--black);
      }
    }

    .health-bar {
      display: flex;
      grid-gap: 40px;

      .bar {
        height: 400px;

        :global(&.bar2 .bar::before),
        :global(&.bar2 .bar::after) {
          background-image: url('../assets/dice/test (2).jpg');
        }

        :global(&.bar3 .bar::before),
        :global(&.bar3 .bar::after) {
          background-image: url('../assets/dice/test (4).jpg');
        }

        :global(&.bar4 .bar::before),
        :global(&.bar4 .bar::after) {
          background-image: url('../assets/dice/test (10).jpg');
        }

        :global(&.bar5 .bar::before),
        :global(&.bar5 .bar::after) {
          background-image: url('../assets/dice/test (12).jpg');
        }

        :global(&.bar6 .bar::before),
        :global(&.bar6 .bar::after) {
          background-image: url('../assets/dice/standard.jpg');
        }
      }

      :global(&.mid .bar::before) {
        mix-blend-mode: color-dodge;
      }
      :global(&.mid .bar::after) {
        mix-blend-mode: color-burn;
      }

      :global(&.dark .bar::before),
      :global(&.dark .bar::after) {
        mix-blend-mode: color-burn;
      }
    }
  }
</style>
